<script lang="ts">
  import { onMount } from 'svelte';
  import Button from '../Button.svelte';
  import Modal from '../Modal.svelte';
  import ReCaptcha from '../ReCaptcha.svelte';
  import type { SendEmailRequest } from '$lib/types/sendEmailRequest';
  import { logger } from '$lib/logger';
  import { ContactUsApiClient } from '$lib/utils/contactUsClient';
  import { isEmailValid } from '$lib/utils/emailValidator';
  import type { Slot } from '$lib/types/public-resources/launches/launch';
  import {
    generateEmailMessage,
    generateRecapMessage
  } from '$lib/components/Launch/generateMessages';
  import Checkbox from '../Checkbox.svelte';
  import AvailableCapacityCard from './AvailableCapacityCard.svelte';
  import { addToast } from '$lib/stores/toastStore';
  import { page } from '$app/stores';

  export let availableCapacity: { slots: Slot[] };

  let isAnySlotRequested: boolean = false;
  let requestedSlots: Array<number> = [];
  let modalTarget: string = 'modal-available-capacity';
  let recaptchaRef: ReCaptcha;
  let availableCapacityCardRef: AvailableCapacityCard;
  let selectedServices: Set<string> = new Set();
  let services: string[] = [
    'Customs Clearance',
    'Insurance',
    'Launch Mission Management',
    'Satellite Preparation',
    'Shipment',
    'Storage'
  ];
  let request: SendEmailRequest = {
    from: '',
    subject: null,
    body: null,
    token: ''
  };

  $: isFormValid = request.token !== '' && isEmailValid(request.from);

  function handleQuantityChange(event: CustomEvent<{ quantity: number }>, index: number) {
    requestedSlots[index] = event.detail.quantity;

    isAnySlotRequested = false;
    requestedSlots.forEach((requestedSlot) => {
      if (requestedSlot > 0) {
        isAnySlotRequested = true;
      }
    });
  }

  async function handleBookNow() {
    request.subject = 'New Slot(s) Booking';
    const idLaunch = $page.params.idLaunch;
    request.body = generateEmailMessage(
      availableCapacity,
      requestedSlots,
      selectedServices,
      idLaunch
    );

    await sendEmail(request);
    resetForm();
  }

  async function sendEmail(request: SendEmailRequest) {
    const client = new ContactUsApiClient();
    try {
      await client.sendEmail(request);
      logger.info('Email sent successfully');
      addToast(
        'Thank you for submitting your booking request. We will get back to you as soon as possible',
        'success'
      );
    } catch (error) {
      logger.error(error);
      addToast(
        'We are sorry, something went wrong while processing your booking request. Please try again. If the problem persists, please contact support@impulso.space',
        'danger',
        8000
      );
    }
  }

  function handleCheckboxChange(event: any, item: string) {
    if (event.target.checked) {
      selectedServices.add(item);
    } else {
      selectedServices.delete(item);
    }
  }

  function loadRecaptchaToken(event: any) {
    request.token = event.detail.token;
  }

  function resetForm() {
    selectedServices = new Set();
    request = {
      from: '',
      subject: null,
      body: null,
      token: ''
    };
    recaptchaRef.reset();
    availableCapacityCardRef.resetQuantitySelector();
  }

  onMount(() => {
    if (availableCapacity) {
      for (let index = 0; index < availableCapacity.slots.length; index++) {
        requestedSlots[index] = 0;
      }
    }
  });
</script>

<div class="row {availableCapacity.slots.length == 1 ? 'justify-content-center' : ''}">
  {#each availableCapacity.slots as slot, index}
    <div class="col-12 col-lg-6">
      <AvailableCapacityCard
        bind:this={availableCapacityCardRef}
        slotIndex={index + 1}
        quantity={slot.quantity}
        capacity={slot.capacity}
        envelope={slot.envelope}
        mechanicalInterfaces={slot.mechanicalInterfaces}
        on:quantityChange={(event) => handleQuantityChange(event, index)}
      ></AvailableCapacityCard>
    </div>
  {/each}
</div>

<div class="row mt-4">
  <div class="ms-auto me-auto col-auto">
    <Button label="Book Now" disabled={!isAnySlotRequested} {modalTarget}>Book Now</Button>
  </div>
</div>

<Modal {modalTarget} on:modalClose={resetForm} modalType="modal-md">
  <div slot="modal-title">&nbsp;</div>

  <div slot="modal-body">
    <div class="form-group">
      <div class="mt-1 mb-4">
        <p class="mb-2 bold">Requested Slots:</p>
        <p class="modal-p">{generateRecapMessage(availableCapacity, requestedSlots)}</p>
      </div>

      <div class="mb-5">
        <p>Let us know the services you're interested in:</p>
        {#each services as service}
          <Checkbox
            id={service}
            name={service}
            checked={selectedServices.has(service)}
            onCheckboxChange={handleCheckboxChange}
          />
        {/each}
      </div>
    </div>

    <label for="email">Your email address</label>
    <input
      bind:value={request.from}
      type="email"
      class="form-control bg-light-black mt-2"
      id="email"
      name="email"
      required
    />
    <ReCaptcha
      bind:this={recaptchaRef}
      additionalClasses="mt-4"
      on:tokenGenerated={loadRecaptchaToken}
    ></ReCaptcha>
  </div>

  <div slot="modal-footer" class="w-100">
    <div class="d-flex justify-content-end">
      <Button
        label={`Book Now`}
        type="primary"
        click={handleBookNow}
        dismissModal={true}
        disabled={!isFormValid}
      >
        Send
      </Button>
    </div>
  </div>
</Modal>

<style>
  .modal-p {
    white-space: pre-wrap;
  }
</style>
